import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React from "react";
import { toast } from "react-toastify";
import {
  MS_LOGIN_EMAIL_KEY,
  MS_LOGIN_PENDING_KEY,
} from "../../auth/authSessionKeys";
import {
  completeMicrosoftLogin,
  getMicrosoftAuthErrorMessage,
} from "../../auth/microsoftAuth";
import {
  getLoginRequest,
  isMicrosoftAuthConfigured,
} from "../../auth/msalConfig";
import { useCheckUserMutation } from "../../features/login/loginapi.services";

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/**
 * check-user response shape:
 * { exists: true, email: "...", message: "User found" }
 */
export function isUserPresentInDb(payload) {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return payload.exists === true;
}

function getCheckUserErrorMessage(error) {
  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.detail ||
    error?.data?.email?.[0] ||
    "Failed to fetch."
  );
}

/**
 * Microsoft SSO control. Uses the email already entered on the login form.
 * Calls check-user first; only starts Entra ID login when the user exists.
 */
const MicrosoftLoginButton = ({
  disabled = false,
  resolveEmail,
  onLoadingChange,
  onSessionReady,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const completingRef = React.useRef(false);
  const [checkUser, { isLoading: isCheckingUser }] = useCheckUserMutation();

  const redirectInProgress =
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.AcquireToken ||
    inProgress === InteractionStatus.Logout;

  React.useEffect(() => {
    onLoadingChange?.(
      redirectInProgress || completingRef.current || isCheckingUser
    );
  }, [isCheckingUser, onLoadingChange, redirectInProgress]);

  const finishLogin = React.useCallback(async () => {
    completingRef.current = true;
    onLoadingChange?.(true);
    try {
      const session = await completeMicrosoftLogin(instance);
      sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);
      onSessionReady?.(session.user);
    } catch (error) {
      toast.error(getMicrosoftAuthErrorMessage(error));
      completingRef.current = false;
      onLoadingChange?.(false);
    }
  }, [instance, onLoadingChange, onSessionReady]);

  React.useEffect(() => {
    if (inProgress !== InteractionStatus.None || completingRef.current) {
      return;
    }

    const pending = sessionStorage.getItem(MS_LOGIN_PENDING_KEY) === "1";
    if (!pending) {
      return;
    }

    if (!accounts.length) {
      sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
      sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);
      return;
    }

    sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
    finishLogin();
  }, [accounts.length, finishLogin, inProgress]);

  const startMicrosoftLogin = async (verifiedEmail) => {
    try {
      sessionStorage.setItem(MS_LOGIN_PENDING_KEY, "1");
      sessionStorage.setItem(MS_LOGIN_EMAIL_KEY, verifiedEmail);
      await instance.loginRedirect(
        getLoginRequest({ loginHint: verifiedEmail })
      );
    } catch (error) {
      sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
      sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);
      toast.error(getMicrosoftAuthErrorMessage(error));
    }
  };

  const handleLogin = async () => {
    if (disabled || redirectInProgress || completingRef.current || isCheckingUser) {
      return;
    }

    // 1. Validate email is entered (and valid) before any API / Microsoft call
    const email = await resolveEmail?.();
    if (!email) {
      return;
    }

    try {
      // 2. Confirm the user exists in the database
      const result = await checkUser({ email }).unwrap();

      if (!isUserPresentInDb(result)) {
        toast.error(
          result?.message ||
            result?.error ||
            result?.detail ||
            "This email is not registered. Microsoft login is not allowed."
        );
        return;
      }

      // 3. Only then start Microsoft Entra ID sign-in
      if (!isMicrosoftAuthConfigured()) {
        toast.error(
          "Microsoft sign-in is not configured. Set the Azure client id, tenant or authority, redirect URI, and API scope."
        );
        return;
      }

      await startMicrosoftLogin(email);
    } catch (error) {
      if (error?.status === 404) {
        toast.error(
          error?.data?.message ||
            "This email is not registered. Microsoft login is not allowed."
        );
        return;
      }

      toast.error(getCheckUserErrorMessage(error));
    }
  };

  return (
    <button
      type="button"
      onClick={handleLogin}
      disabled={disabled || redirectInProgress || isCheckingUser}
      aria-label="Login with Microsoft"
      className="flex h-12 w-full flex-row items-center justify-center gap-2 rounded-lg border border-[#3E3E3E] bg-white px-2 text-[#232323] transition hover:border-background-primary-main hover:bg-background-primary-light disabled:cursor-not-allowed disabled:opacity-50"
    >
      <MicrosoftLogo />
      <span className="text-xs font-semibold sm:text-sm">
        {isCheckingUser ? "Checking..." : "Microsoft"}
      </span>
    </button>
  );
};

export default MicrosoftLoginButton;
